import { capitalize } from '@gsc/utils'
import { EventType } from './tracking'

type EventTypeKey = keyof typeof EventType

export enum ARG_TYPES {
  Settings = 'settings',

  Params = 'params', // gsc('params', {email: 'user@example.com', name: 'Alex'})
  Callback = 'callback', // gsc('onSubmit', (widgetId, data, ctx) => {})

  TrackPage = 'trackPage', // gsc('trackPage', 'https://example.com/checkout')
  ShowWidget = 'show', // gsc('show', 777)
  CloseWidget = 'close', // gsc('close', 777)
  HideWidget = 'hide', // gsc('hide', 777)
}

export class Arg {
  public value: any = null
  public callback: () => any

  constructor(public type: string) {}

  public isHandlers(): boolean {
    return this.type == ARG_TYPES.Callback
  }

  public isData(): boolean {
    return this.type == ARG_TYPES.Params || this.type == ARG_TYPES.Settings
  }

  public isMethod(): boolean {
    return (
      this.type == ARG_TYPES.ShowWidget ||
      this.type == ARG_TYPES.CloseWidget ||
      this.type == ARG_TYPES.HideWidget ||
      this.type == ARG_TYPES.TrackPage
    )
  }

  public static parse(arg: any): Arg {
    if (Array.isArray(arg)) {
      // [params, username, anton] or
      // [params, {plan: gold}]

      // try transform onSubmit -> callback,submit
      arg = this.transformOnEvent(arg)

      if (arg.length < 2) {
        throw new Error('invalid arg array length')
      }
      const a = new Arg(arg[0])
      if (a.isData()) {
        if (typeof arg[1] === 'object') {
          a.value = { ...arg[1] }
        } else if (arg.length >= 3) {
          a.value = { [arg[1]]: arg[2] }
        } else {
          throw new Error('invalid arg array')
        }
      } else if (a.isHandlers()) {
        if (arg.length < 3) {
          throw new Error('invalid handler arg')
        }
        if (typeof arg[1] !== 'string') {
          throw new Error('arg handler type invalid')
        }
        if (typeof arg[2] !== 'function') {
          throw new Error('arg not a function')
        }
        a.value = arg[1]
        a.callback = arg[2]
      } else if (a.isMethod()) {
        a.value = arg[1]
      } else {
        throw new Error('unknown arg type ' + a.type)
      }

      return a
    } else {
      throw new Error('unknown arg format')
    }
  }

  public static transformOnEvent(args: any[]): any[] {
    if (args.length === 2 && typeof args[0] === 'string') {
      // see https://github.com/Microsoft/TypeScript/issues/3500
      for (const k in EventType) {
        if (args[0] === `on${capitalize(EventType[k as EventTypeKey])}`) {
          return ['callback', EventType[k as EventTypeKey], args[1]]
        }
      }
    }
    return args
  }
}

export interface IRuntimeArgs {
  getParams(primary?: boolean): { [key: string]: any }

  runCallbacks(name: string, ...args: any): any
}

export class RuntimeArgs implements IRuntimeArgs {
  private settings: { [key: string]: any } = {}
  private params: { [key: string]: any } = {}
  public callbacks: { [key: string]: any[] } = {}

  public addNew(arg: any): Arg {
    const a = Arg.parse(arg)

    //store
    if (a.isData()) {
      // fixme: deep merge?
      Object.assign(this.getCollection(a.type), a.value)
    } else if (a.isHandlers()) {
      const handlers = this.getCollection(a.type),
        typeHandlers = (handlers[a.value] = handlers[a.value] || [])

      if (typeHandlers.indexOf(a.callback) == -1) {
        typeHandlers.push(a.callback)
      }
    }

    return a
  }

  private getCollection(type: string) {
    if (type == ARG_TYPES.Settings) {
      return this.settings
    }
    if (type == ARG_TYPES.Params) {
      return this.params
    }
    if (type == ARG_TYPES.Callback) {
      return this.callbacks
    }
    throw new Error('unknown type')
  }

  public getParams(primary?: boolean): { [p: string]: any } {
    const resp: any = {}
    for (const k in this.params || {}) {
      if (
        primary === undefined ||
        (primary === true && !k.startsWith('_')) ||
        (primary === false && k.startsWith('_'))
      ) {
        resp[k] = this.params[k]
      }
    }
    return resp
  }

  public runCallbacks(name: string, ...args: any) {
    console.log(`run '${name}' callbacks `, ...args)
    const callbacks = this.callbacks[name] || []
    callbacks.forEach(callback => callback(...args))
  }
}
